import React, { useEffect, useState } from 'react';
import styles from './CampaignManagerInfluencers.module.css';
import SearchInput from '../../../ui/SearchInput/SearchInput';
import useUrlState from '../../../hooks/useUrlState';
import Skeleton from '../../../ui/Skeleton/Skeleton';
import range from 'lodash/range';
import useOnScroll from '../../../hooks/useOnScroll';
import Badge from '../../../ui/Badge/Badge';
import { useCampaignManagerInfluencersData } from './useCampaignManagerInfluencersData';
import CampaignManagerInfluencerBar from './influencerBar/CampaignManagerInfluencerBar';
import useAbortableEffect from '../../../hooks/useAbortableEffect';
import { showNotification } from '../../../utils/showNotification';
import useNonNullContext from '../../../hooks/useNonNullContext';
import { OptionsContext } from '../../../contexts/OptionsContext';
import EditInfluencerModal from './influencerModals/EditInfluencerModal';
import CreateInfluencerModal from './influencerModals/CreateInfluencerModal';
import Button from '../../../ui/Button/Button';

type UrlState = {
    search: string;
};

const PAGE_SIZE = 25;

const CampaignManagerInfluencers = () => {
    const [urlState, setUrlState] = useUrlState<UrlState>({ search: '' });

    const { currencies } = useNonNullContext(OptionsContext);
    const DEFAULT_CURRENCY = currencies.find((curr) => curr.name === 'USD');

    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [influencerToEditId, setInfluencerToEditId] = useState<number | null>(null);

    const {
        influencers,
        influencerCosts,
        influencerCount,
        hasNextInfluencersPage,
        isLoading,
        hasErrorLoadingData,
        isInitialized,
        tiktokUserImages,
        instagramUserImages,
        init,
        reset,
        loadNextPage,
        updateInfluencers,
        refreshInfluencerCosts,
        addInfluencer,
        addCosts,
    } = useCampaignManagerInfluencersData({
        search: urlState.search,
        page_size: PAGE_SIZE,
    });

    useAbortableEffect(
        (signal) => {
            if (!isInitialized) {
                init({ signal }).catch(() => showNotification('Could not load data', 'error'));
            }
        },
        [init, isInitialized]
    );

    const onBottomReached = useOnScroll(
        () => {
            if (!isLoading && !hasErrorLoadingData && hasNextInfluencersPage) {
                loadNextPage().catch(() => showNotification('Could not load next page', 'error'));
            }
        },
        { threshold: 500 }
    );

    useEffect(() => {
        document.getElementById('root')?.addEventListener('scroll', onBottomReached);
        return () => document.getElementById('root')?.removeEventListener('scroll', onBottomReached);
    }, [onBottomReached]);

    const influencerCountDisplayValue = `${influencerCount} Creator${
        typeof influencerCount === 'number' && influencerCount !== 1 ? 's' : ''
    }`;

    const isListLoading = isLoading || (!isInitialized && !hasErrorLoadingData);

    return (
        <>
            <div className={styles.main}>
                <div className={styles.headerBar}>
                    <SearchInput
                        className={styles.search}
                        value={urlState.search}
                        onChange={(search) => {
                            setUrlState({ search });
                            reset();
                        }}
                    />

                    {!hasErrorLoadingData && (
                        <Badge className={styles.countDisplayBadge} loading={isListLoading}>
                            {influencerCountDisplayValue}
                        </Badge>
                    )}

                    <Button className={styles.addInfluencerButton} onClick={() => setIsCreateModalOpen(true)}>
                        Add New Creator
                    </Button>
                </div>

                <div className={styles.list}>
                    {influencers.map((inf) => {
                        const image =
                            tiktokUserImages.find((image) => image.user_id === inf.tiktok_user_id) ??
                            instagramUserImages.find((image) => image.user_id === inf.instagram_user_id);

                        const currencyToUse =
                            currencies.find((curr) => curr.id === inf.preferred_currency) || DEFAULT_CURRENCY;

                        const costs = influencerCosts.filter(
                            (cost) => cost.influencer === inf.id && cost.currency === currencyToUse?.id
                        );

                        return (
                            <CampaignManagerInfluencerBar
                                key={inf.id}
                                influencer={inf}
                                influencerCosts={costs}
                                currency={currencyToUse}
                                image={image?.avatar_thumb}
                                onClickEdit={(id: number) => {
                                    setInfluencerToEditId(id);
                                    setIsEditModalOpen(true);
                                }}
                            />
                        );
                    })}
                    {isListLoading &&
                        range(0, 4).map((i) => (
                            <Skeleton
                                key={i}
                                baseColor="#fbfbfb"
                                highlightColor="#F5F6F8"
                                containerClassName={styles.loadingBarContainer}
                                className={styles.loadingBar}
                            />
                        ))}
                </div>
            </div>

            <CreateInfluencerModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreated={({ influencer, costs }) => {
                    showNotification('Creator added', 'info');
                    addInfluencer({
                        id: influencer.id,
                        instagram_user_id: influencer.instagram_user_id,
                        tiktok_user_id: influencer.tiktok_user_id,
                        name: influencer.name,
                        tiktok_username: influencer.tiktok_username,
                        instagram_username: influencer.instagram_username,
                        preferred_currency: influencer.preferred_currency,
                        instagram_display_name: influencer.name,
                        tiktok_display_name: influencer.name,
                        youtube_username: influencer.youtube_username,
                        is_available_for_booking: influencer.is_available_for_booking,
                    });
                    addCosts(costs);
                }}
            />

            <EditInfluencerModal
                influencerId={influencerToEditId}
                influencerCosts={influencerCosts.filter((cost) => cost.influencer === influencerToEditId)}
                isOpen={isEditModalOpen}
                onClose={() => {
                    setInfluencerToEditId(null);
                    setIsEditModalOpen(false);
                }}
                onUpdated={({ influencer, costsChanged }) => {
                    if (influencer) {
                        updateInfluencers({
                            id: influencer.id,
                            instagram_user_id: influencer.instagram_user_id,
                            name: influencer.name,
                            tiktok_display_name: influencer.name,
                            tiktok_username: influencer.tiktok_username,
                            instagram_username: influencer.instagram_username,
                            preferred_currency: influencer.preferred_currency,
                            instagram_display_name: influencer.name,
                            youtube_username: influencer.youtube_username,
                            tiktok_user_id: influencer.tiktok_user_id,
                            is_available_for_booking: influencer.is_available_for_booking,
                        });
                    }

                    if (costsChanged && typeof influencerToEditId === 'number') {
                        refreshInfluencerCosts(influencerToEditId).catch((e) => showNotification(e.message, 'error'));
                    }
                }}
            />
        </>
    );
};

export default CampaignManagerInfluencers;
